import React from "react";
import { NavigationType } from "../../models/enums/NavigationType";
import { DispositionCode } from "../../models/enums/DispositionCode";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ThemeModel } from "../../models/ThemeModel";
import { getSettingString, getSettingEnum } from "../../models/utilities/settingUtilities";
import { Signal } from "../../signals/index";
import { Voxco } from "../../public";
import { ValidationEvent } from "../../public/events";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { QuitButtonAction } from "../../models/enums/QuitButtonAction";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import PageRenderer from "../renderers/v2/PageRenderer";
import { ThunkDispatchProp } from "../../models/actions/ThunkDispatchProp";
import {
    validationClear,
    validationSetResult,
    navigationPreviousPage,
    navigationBranchTo,
    navigationQuit,
    navigationNextPage,
} from "../../redux/actions";
import {
    validate,
    getVisibleQuestions,
    validateAndNavigateToNextPage,
} from "../../redux/thunks/validation";
import { isValid } from "../../models/utilities/validationUtilities";
import { QuestionModel } from "../../models/QuestionModel";

interface Props extends ThunkDispatchProp {
    isValid: boolean;
    questions: QuestionModel[];
    message: string | Error;
    language: string;
    validation: ValidationResultModel;
    questionnaire: QuestionnaireModel;
    matrixIndex: number;
    isNavigationDisabled: boolean;
    progress: number;
    title: string;
    theme: ThemeModel;
    useClientValidation: boolean;
    isDesktopPreviewView?: boolean;
}

// Set this callback to the current page's validation function.
let validationCallback: (ev?: ValidationEvent) => ValidationResultModel;

// Set up page validation event.
Voxco.on("validatePage", (ev) => (validationCallback ? validationCallback(ev) : ev));

export class Page extends React.PureComponent<Props> {
    validateSignal = new Signal<ValidationOptionsModel, ValidationResultModel>();

    componentDidMount() {
        validationCallback = this.onValidate;
        this.resizeImages();
        this.setViewportContent();
        window.onresize = () => this.resizeImages();
    }

    onValidate = (ev: ValidationEvent): ValidationResultModel => {
        // Prevent default behavior.
        if (ev.defaultPrevented) {
            return new ValidationBuilder().build();
        }
        const results = this.validateSignal.dispatch(ev.options);
        const result = results.reduce(
            (a, b) => ({
                isValid: a.isValid && isValid(b),
                errorsByVariableName: { ...a.errorsByVariableName, ...b.errorsByVariableName },
            }),
            { isValid: true, errorsByVariableName: {} },
        );
        // Set the current state if the validation is not silent.
        if (!ev.options || !ev.options.silent) {
            const { dispatch } = this.props;
            dispatch(validationClear(false, true));
            dispatch(validationSetResult(result));
        }
        return result;
    };

    onNavigation = (type: NavigationType) => {
        const { questionnaire, isNavigationDisabled, useClientValidation, dispatch } = this.props;
        switch (type) {
            case NavigationType.Next:
            case NavigationType.Submit:
                if (isNavigationDisabled) {
                    break;
                }
                if (!useClientValidation) {
                    Voxco.validatePage({ silent: true });
                    dispatch(navigationNextPage());
                    break;
                }
                if (
                    process.env.DISABLE_CLIENT_VALIDATION !== "true" &&
                    !Voxco.trigger("validatePage").isValid
                ) {
                    const questions = dispatch(getVisibleQuestions());
                    dispatch(validate(questions));
                    break;
                }
                dispatch(validateAndNavigateToNextPage());
                break;
            case NavigationType.Back:
                if (isNavigationDisabled) {
                    break;
                }
                dispatch(navigationPreviousPage());
                break;
            case NavigationType.Quit: {
                const action = getSettingEnum(
                    questionnaire,
                    QuitButtonAction,
                    "QUIT_BUTTON_ACTION",
                );
                const destination = getSettingString(
                    questionnaire,
                    "QUIT_BUTTON_BRANCH_TO_QUESTION",
                );
                if (destination && action === QuitButtonAction.BranchTo) {
                    dispatch(navigationBranchTo({ destination, isQuitButton: true }));
                } else {
                    dispatch(navigationQuit(DispositionCode.Interrupted, { forcedQuit: true }));
                }
                break;
            }
            default:
                break;
        }
    };

    setViewportContent() {
        if (window.navigator.appVersion.includes("iPhone")) {
            const viewport = document.getElementsByName("viewport")[0];
            const content = viewport.getAttribute("content");
            viewport.setAttribute("content", `${content}, maximum-scale=1`);
        }
    }

    resizeImages() {
        ["pageHeader", "pageFooter"].forEach((c) => {
            Array.from(document.getElementsByClassName(c)).forEach((element: HTMLElement) => {
                Array.from(element.getElementsByTagName("img")).forEach((imgElement) => {
                    const maxHeightPercent = 25;
                    const currentHeightPercent = (imgElement.height / window.innerHeight) * 100;
                    if (currentHeightPercent !== maxHeightPercent) {
                        imgElement.style.maxHeight = `${
                            (window.innerHeight * maxHeightPercent) / 100
                        }px`;
                        imgElement.style.maxWidth = "100%";
                        imgElement.style.height = imgElement.style.height || "auto";
                        imgElement.style.width = imgElement.style.width || "auto";
                    }
                });
            });
        });
    }

    render() {
        return (
            <PageRenderer
                onNavigation={this.onNavigation}
                validateSignal={this.validateSignal}
                {...this.props}
            />
        );
    }
}
